import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Settings, Plus, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { useSede } from '@/lib/sede';

const roleLabels = { coordinatore: 'Coordinatore', medico: 'Medico', infermiere: 'Infermiere', oss: 'OSS', amministrazione: 'Amministrazione' };
const roleColors = { coordinatore: 'bg-purple-100 text-purple-700', medico: 'bg-blue-100 text-blue-700', infermiere: 'bg-emerald-100 text-emerald-700', oss: 'bg-amber-100 text-amber-700', amministrazione: 'bg-slate-100 text-slate-700' };

export default function Utenti() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', full_name: '', role: 'infermiere', password: '', sede_ids: [] });
  const { sedi } = useSede();

  const sedeLabel = (u) => {
    const ids = u.sede_ids || [];
    if (!ids.length) return 'Tutte le sedi';
    return ids.map(id => sedi.find(s => s.id === id)?.nome || 'Sede').join(', ');
  };

  const handleSetSede = async (userId, value) => {
    try {
      await api.put(`/auth/users/${userId}`, { sede_ids: value === 'tutte' ? [] : [value] });
      toast.success('Sede aggiornata');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const fetchUsers = () => {
    api.get('/auth/users').then(r => setUsers(r.data)).catch(() => toast.error('Errore caricamento utenti')).finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async () => {
    if (!form.username || !form.full_name || !form.password) { toast.error('Compila i campi obbligatori'); return; }
    try {
      await api.post('/auth/users', form);
      toast.success('Utente creato');
      setShowForm(false);
      setForm({ username: '', email: '', full_name: '', role: 'infermiere', password: '', sede_ids: [] });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore creazione'); }
  };

  const handleToggleActive = async (userId, active) => {
    try {
      if (active) {
        await api.delete(`/auth/users/${userId}`);
        toast.success('Utente disattivato');
      } else {
        await api.put(`/auth/users/${userId}`, { active: true });
        toast.success('Utente riattivato');
      }
      fetchUsers();
    } catch (e) { toast.error('Errore'); }
  };

  if (user?.role !== 'coordinatore') {
    return <div className="text-center py-20"><Settings className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Accesso riservato al coordinatore</p></div>;
  }

  return (
    <div className="space-y-6 max-w-[1200px] mx-auto" data-testid="utenti-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight">Gestione Utenti</h1>
          <p className="text-sm text-slate-500 mt-0.5">{users.length} utenti registrati</p>
        </div>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="bg-brand hover:bg-brand-dark text-white"><Plus className="w-4 h-4 mr-2" />Nuovo Utente</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuovo Utente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Username *</Label><Input data-testid="new-user-username" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} /></div>
              <div><Label className="text-xs">Nome Completo *</Label><Input data-testid="new-user-name" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
              <div><Label className="text-xs">Ruolo</Label>
                <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(roleLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Sede</Label>
                <Select value={form.sede_ids[0] || 'tutte'} onValueChange={v => setForm(f => ({ ...f, sede_ids: v === 'tutte' ? [] : [v] }))}>
                  <SelectTrigger data-testid="new-user-sede"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutte">Tutte le sedi</SelectItem>
                    {sedi.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Password *</Label><Input data-testid="new-user-password" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} /></div>
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button><Button data-testid="save-new-user" onClick={handleCreate} className="bg-brand hover:bg-brand-dark text-white">Crea Utente</Button></div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="animate-pulse p-8"><div className="h-4 bg-slate-200 rounded w-full mb-4"></div><div className="h-4 bg-slate-200 rounded w-3/4"></div></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Ruolo</TableHead>
                  <TableHead>Sede</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium text-sm">{u.full_name}</TableCell>
                    <TableCell className="text-sm font-mono">{u.username}</TableCell>
                    <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                    <TableCell><Badge className={`text-[10px] ${roleColors[u.role] || ''}`}>{roleLabels[u.role] || u.role}</Badge></TableCell>
                    <TableCell>
                      <Select value={(u.sede_ids || [])[0] || 'tutte'} onValueChange={v => handleSetSede(u.id, v)}>
                        <SelectTrigger className="h-7 text-[11px] w-[160px]" data-testid={`user-sede-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tutte">Tutte le sedi</SelectItem>
                          {sedi.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {u.active !== false ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><UserCheck className="w-3 h-3 mr-1" />Attivo</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 text-[10px]"><UserX className="w-3 h-3 mr-1" />Disattivato</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.id !== user.id && (
                        <Button variant="ghost" size="sm" className="text-xs" onClick={() => handleToggleActive(u.id, u.active !== false)} data-testid={`toggle-user-${u.id}`}>
                          {u.active !== false ? 'Disattiva' : 'Riattiva'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
